import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { listLeads } from '../lib/resources';
import type { Lead, LeadKind } from '../types';
import { inputClass } from './ui';

/** Busca de lead por nome, e-mail ou documento — "Buscar empresa...", "Vincular pessoa". */
export function LeadPicker({
  kind,
  value,
  onChange,
  placeholder,
  excludeIds = [],
}: {
  kind?: LeadKind;
  value: { id: string; name: string } | null;
  onChange: (lead: { id: string; name: string } | null) => void;
  placeholder?: string;
  excludeIds?: string[];
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['lead-picker', kind, term],
    queryFn: () => listLeads({ search: term || undefined, take: 10, kind }),
    enabled: open,
  });
  const options = (query.data?.data ?? []).filter(
    (lead: Lead) => !excludeIds.includes(lead.id),
  );

  if (value) {
    return (
      <div className="mt-1 flex items-center justify-between rounded-lg border border-slate-300 px-3 py-2 text-sm">
        <span className="truncate text-slate-800">{value.name}</span>
        <button
          type="button"
          className="text-xs text-slate-400 hover:text-slate-700"
          onClick={() => onChange(null)}
        >
          Trocar
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className={inputClass}
        placeholder={placeholder ?? 'Buscar...'}
        value={term}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(event) => setTerm(event.target.value)}
      />
      {open && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {query.isLoading && <li className="px-3 py-2 text-xs text-slate-400">Buscando…</li>}
          {!query.isLoading && options.length === 0 && (
            <li className="px-3 py-2 text-xs text-slate-400">Nenhum lead encontrado.</li>
          )}
          {options.map((lead) => (
            <li key={lead.id}>
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange({ id: lead.id, name: lead.name });
                  setTerm('');
                  setOpen(false);
                }}
              >
                <span className="font-medium text-slate-800">{lead.name}</span>
                <span className="block text-xs text-slate-400">
                  {[lead.tradeName, lead.email, lead.taxId].filter(Boolean).join(' · ') || '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
